import type { DisciplineDemoFeedback } from '../interfaces'
import type { DisciplineDemoDetails, DisciplineReportItem } from '../discipline-report'

import { Type as BookingType } from '@brighterly/lib-core-types/booking/enums'

import { useListsStore } from '@shared/model'

import { useLessonEditStore } from './edit/store'
import { useDisciplineStore } from '../store'
import { buildDemoDetails, buildDemoKidInfo, buildDemoParentInfo } from '../discipline-report'
import { buildPaidFeedbackRows, hasLessonFeedback } from './lesson-feedback'

interface LessonFeedbackReturn {
  refHasFeedback: ComputedRef<boolean>
  refIsPaid: ComputedRef<boolean>
  refPaidRows: ComputedRef<DisciplineReportItem[]>
  refParentInfo: ComputedRef<DisciplineReportItem[]>
  refKidInfo: ComputedRef<DisciplineReportItem[]>
  refDemoDetails: ComputedRef<DisciplineDemoDetails>
}

/** A paid lesson shows the tutor's lesson report; a demo lesson the same demo report as the Demo lesson report tab. */
export const useLessonFeedback = (): LessonFeedbackReturn => {
  const store = useLessonEditStore()
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()

  const refFeedback = computed<Record<string, unknown> | null>(() => store.getLesson?.tutor_feedback ?? null)

  const refDemoFeedback = computed<DisciplineDemoFeedback | null>(() => refFeedback.value as DisciplineDemoFeedback | null)

  const refHasFeedback = computed<boolean>(() => hasLessonFeedback(refFeedback.value))

  const refIsPaid = computed<boolean>(() => store.getLesson?.type === BookingType.Paid)

  const refPaidRows = computed<DisciplineReportItem[]>(() => buildPaidFeedbackRows(
    refFeedback.value,
    { grades: listsStore.getLists?.kid.grade ?? {} },
    store.getLesson?.tutor_notes ?? null,
  ))

  const refParentInfo = computed<DisciplineReportItem[]>(() => buildDemoParentInfo(refDemoFeedback.value))

  const refKidInfo = computed<DisciplineReportItem[]>(() => buildDemoKidInfo(refDemoFeedback.value, {
    genders: listsStore.getLists?.genders ?? {},
    grades: listsStore.getLists?.kid.grade ?? {},
  }))

  const refDemoDetails = computed<DisciplineDemoDetails>(() =>
    buildDemoDetails(disciplineStore.getDiscipline?.code ?? '', refDemoFeedback.value))

  return {
    refHasFeedback,
    refIsPaid,
    refPaidRows,
    refParentInfo,
    refKidInfo,
    refDemoDetails,
  }
}
